import React, { useEffect, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faUser,
    faEnvelope,
    faGlobe,
    faShieldAlt,
    faCrown,
    faCreditCard,
    faCalendarAlt,
    faCheckCircle,
    faTimesCircle,
    faUserTag,
    faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { post } from '../../../helpers/apiHelper';

const UserDetailModal = ({ user, onClose }) => {
    const [resendingEmail, setResendingEmail] = useState(false);

    // Manejar cierre con Escape
    const handleEscape = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [handleEscape]);

    // Prevenir scroll del body cuando el modal está abierto
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleResendVerification = async () => {
        try {
            setResendingEmail(true);
            await post('auth/resend-verification', { email: user.email });
            alert('Email de verificación reenviado con éxito');
        } catch (error) {
            console.error('Error resending verification email:', error);
            alert('Error al reenviar el email de verificación: ' + (error.message || 'Error desconocido'));
        } finally {
            setResendingEmail(false);
        }
    };

    if (!user) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end sm:items-center justify-center min-h-screen p-0 sm:p-4 text-center sm:block">
                {/* Overlay de fondo */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                {/* Centrado del modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                {/* Modal */}
                <div className="inline-block w-full align-bottom sm:align-middle bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                        <div className="flex justify-between items-center pb-3 border-b">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-900" id="modal-title">
                                Detalles del Usuario
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-500 transition-colors p-2 sm:p-0"
                                aria-label="Cerrar"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>

                        {/* Contenido */}
                        <div className="mt-4 space-y-4 sm:space-y-6">
                            {/* Información básica */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">Nombre completo</p>
                                            <p className="font-medium break-words">
                                                {user.nombre} {user.apellido} {user.segundoapellido || ''}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faEnvelope} className="text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-500">Email</p>
                                            <p className="font-medium break-words">{user.email}</p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                                <span className={`text-xs px-2 py-1 rounded-full ${user.isEmailVerified
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    <FontAwesomeIcon
                                                        icon={user.isEmailVerified ? faCheckCircle : faTimesCircle}
                                                        className="mr-1"
                                                    />
                                                    {user.isEmailVerified ? 'Verificado' : 'Pendiente de verificación'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faGlobe} className="text-purple-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">País</p>
                                            <p className="font-medium break-words">{user.pais}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faShieldAlt} className="text-indigo-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">Rol</p>
                                            <span className={`inline-block px-2 py-1 rounded-full text-sm ${user.role === 'ADMIN_ROLE'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {user.role === 'ADMIN_ROLE' ? 'Administrador' : 'Usuario'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faCrown} className="text-yellow-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">Plan</p>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className={`inline-block px-2 py-1 rounded-full text-sm ${{
                                                        'trial': 'bg-gray-100 text-gray-800',
                                                        'basic': 'bg-green-100 text-green-800',
                                                        'professional': 'bg-blue-100 text-blue-800',
                                                        'enterprise': 'bg-purple-100 text-purple-800'
                                                    }[user.plan]
                                                    }`}>
                                                    {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                                                </span>
                                                {user.pagoPlan && (
                                                    <span className="text-sm text-gray-500">
                                                        ({user.pagoPlan === 'monthly' ? 'Mensual' : 'Anual'})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faCreditCard} className="text-red-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">Estado de pago</p>
                                            <span className={`inline-block px-2 py-1 rounded-full text-sm ${user.hasPaid
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                <FontAwesomeIcon
                                                    icon={user.hasPaid ? faCheckCircle : faTimesCircle}
                                                    className="mr-1"
                                                />
                                                {user.hasPaid ? 'Pagado' : 'Pendiente'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fechas */}
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                                <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Fechas</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-start sm:items-center gap-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-500">Registro</p>
                                            <p className="font-medium break-words">{formatDate(user.register_at)}</p>
                                        </div>
                                    </div>

                                    {user.trialStarted && (
                                        <div className="flex items-start sm:items-center gap-3">
                                            <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                                                <FontAwesomeIcon icon={faCrown} className="text-purple-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm text-gray-500">Inicio Trial</p>
                                                <p className="font-medium break-words">{formatDate(user.trialStarted)}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sección de Arquetipo */}
                            {user.archetypeQuiz && (
                                <div className="bg-indigo-50 p-3 sm:p-4 rounded-lg">
                                    <div className="flex items-start sm:items-center gap-2 mb-4">
                                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon={faUserTag} className="text-indigo-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-base sm:text-lg font-medium text-gray-900">Perfil de Arquetipo</h4>
                                            <p className="text-sm text-gray-500">Resultados del análisis de arquetipo</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Nombre del Perfil */}
                                        <div className="flex items-start sm:items-center gap-2">
                                            <FontAwesomeIcon icon={faCrown} className="text-indigo-500 mt-1 sm:mt-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-700">Perfil</p>
                                                <p className="text-base text-indigo-700 break-words">{user.archetypeQuiz.nombrePerfil}</p>
                                            </div>
                                        </div>

                                        {/* Nivel y Subnivel */}
                                        <div className="flex items-start sm:items-center gap-2">
                                            <FontAwesomeIcon icon={faChartLine} className="text-indigo-500 mt-1 sm:mt-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-700">Nivel de Digitalización</p>
                                                <p className="text-base text-indigo-700 break-words">{user.archetypeQuiz.nivel}</p>
                                                <p className="text-sm text-gray-600">Subnivel: {user.archetypeQuiz.subnivel}</p>
                                            </div>
                                        </div>

                                        {/* Puntuación */}
                                        <div className="flex items-start sm:items-center gap-2">
                                            <FontAwesomeIcon icon={faChartLine} className="text-indigo-500 mt-1 sm:mt-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-700">Puntuación Total</p>
                                                <p className="text-base text-indigo-700">{user.archetypeQuiz.puntuacionTotal} puntos</p>
                                            </div>
                                        </div>

                                        {/* Descripción del Perfil */}
                                        <div className="mt-4 p-3 bg-white rounded-lg">
                                            <p className="text-sm font-medium text-gray-700 mb-2">Descripción del Perfil</p>
                                            <p className="text-sm text-gray-600 break-words">{user.archetypeQuiz.descripcionPerfil}</p>
                                        </div>

                                        {/* Fecha de Completado */}
                                        <div className="flex items-start sm:items-center gap-2 mt-2 text-sm text-gray-500">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-500 mt-1 sm:mt-0" />
                                            <span className="break-words">Completado el: {formatDate(user.archetypeQuiz.completedAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 flex flex-col sm:flex-row-reverse gap-2 sm:gap-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full sm:w-auto px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailModal; 